import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import {
    DateTimeService,
    DateTimeUtc,
    DateTimeUtcService,
    DateTimeUtils,
    TimeService,
    WallClockDate,
    WallClockDateService,
} from '@mews/framework/datetime';
import { Optional } from '@mews/std';
import { CalendarService, StructuredDate } from '@optimus-web/core';
import { HOURS_IN_24_FORMAT_DAY } from '@optimus-web/core/src/utils/services/constants';

interface DateStringToUtcOptions {
    dateString: string;
    timezone: string;
}

export const dateStringToUtc = ({ dateString, timezone }: DateStringToUtcOptions): DateTimeUtc =>
    pipe(
        dateString,
        (date) => WallClockDateService.parse(date),
        (date) => WallClockDateService.toTimezonedDate(date, timezone),
        (date) => DateTimeService.fromDate(date),
        (date) => DateTimeService.toUtc(date)
    );

interface IsOccupiedDateOptions {
    calendarService: CalendarService;
    timezone: string;
    occupiedDates: Set<string>;
    date: StructuredDate;
    isHourlyService?: boolean;
}

export const isOccupiedDate = ({
    calendarService,
    timezone,
    occupiedDates,
    date,
    isHourlyService = false,
}: IsOccupiedDateOptions) => {
    const dateString = calendarService.toISOString(date);
    const utcMidnight = dateStringToUtc({ dateString, timezone });
    return pipe(
        // checking all 24 hours for hourly services and only midnight for daily services
        array.makeBy(isHourlyService ? HOURS_IN_24_FORMAT_DAY : 1, (hour) => hour),
        array.every((hour) => {
            const utcWithHours = DateTimeUtcService.addHours(utcMidnight, hour);
            const utcStringWithoutMilliseconds = DateTimeUtcService.toISOString(utcWithHours, {
                suppressMilliseconds: true,
            });
            return occupiedDates.has(utcStringWithoutMilliseconds);
        })
    );
};

export interface GetDisabledReasonModifiers {
    startDate?: StructuredDate | null;
    endDate?: StructuredDate | null;
    categoryId?: string;
    startTimeInHours?: number;
    endTimeInHours?: number;
}

export const startHours = array.makeBy(HOURS_IN_24_FORMAT_DAY, (hour) => hour);
// endHours includes 25 hours to allow selecting times that go into the next day (over midnight)
export const endHours = array.makeBy(HOURS_IN_24_FORMAT_DAY + 1, (hour) => hour + 1);
export const midnight = TimeService.parse('00:00:00');

export const getStructuredDateFromDateString = (calendarService: CalendarService, date: Optional<WallClockDate>) =>
    date && date.value ? DateTimeUtils.wallClockDateToStructuredDate(date, calendarService) : undefined;
